import { FontAwesome6 } from '@expo/vector-icons';
import MuxPlayer, { MuxPlayerRefAttributes } from '@mux/mux-player-react';
import { router, useFocusEffect, useNavigation } from 'expo-router';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { StyleSheet, View } from 'react-native';

import { useGetAllShows, useGetInfluencerDiscountAsync, useGetUserPurchasedShows } from '@/api/hooks/showHooks';
import { InfluencerDiscount } from '@/api/models/showModels';
import BrandedButton from '@/components/buttons/BrandedButton';
import DotsLoader from '@/components/DotsLoader';
import Text from '@/components/Text';
import Title from '@/components/Title';
import Countdown from '@/components/trbShow/Countdown';
import { CHROMECAST_APPLICATION_ID } from '@/constants/config';
import { useAuth } from '@/contexts/authContext';
import { applyDiscount } from '@/utils/applyDiscount';
import { AppStorage } from '@/utils/AppStorage';
import { encryptObject } from '@/utils/crypto';
import { formatDateYear } from '@/utils/dateFormats';
import { i18n } from '@/utils/i18n';

const Purchase = () => {
  const { dispatch } = useAuth();
  const navigation = useNavigation();

  const {
    data: userPurchsaedShows,
    isPending: userShowsPending,
    refetch: refetchUserPurchasedShows
  } = useGetUserPurchasedShows();
  const { data: show, isPending: allShowsPending } = useGetAllShows();
  const { mutateAsync: getInfluencerDiscountAsync, isPending: influencerDiscountPending } =
    useGetInfluencerDiscountAsync();

  const [influencerDiscountState, setInfluencerDiscountState] = useState<InfluencerDiscount>();

  const now = new Date();
  const beDate = show?.releaseDate?.replace(' ', 'T');

  const muxRef = useRef<MuxPlayerRefAttributes>(null);

  useLayoutEffect(() => {
    navigation.setOptions({
      headerTitle: () => <Title label={show?.title ?? 'The Religion Business'} />
    });
  }, [navigation, show]);

  useEffect(() => {
    // Already going to buy
    dispatch({
      type: 'BUYING_SHOW',
      isBuyingShow: false
    });
    const getInfluencerCode = async () => {
      const value = await AppStorage.getItem('influencerCode');
      if (value) {
        getInfluencerDiscountAsync(
          { influencerCode: value },
          {
            onSuccess: (data) => {
              setInfluencerDiscountState(data);
            }
          }
        );
      } else {
        setInfluencerDiscountState(undefined);
      }
    };

    getInfluencerCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useFocusEffect(
    useCallback(() => {
      // Screen gains focus again
      refetchUserPurchasedShows();

      // Screen lost focus
      return () => {
        // Pause video
        muxRef.current?.pause?.();
      };
    }, [refetchUserPurchasedShows])
  );

  // Navigate to show if is already purchased and show has been released
  useEffect(() => {
    if (userPurchsaedShows && userPurchsaedShows.length !== 0 && show) {
      if (!beDate) return;
      const releaseDate = new Date(beDate);
      const isReleased = now.getTime() >= releaseDate.getTime();

      if (isReleased) {
        router.replace({
          pathname: '/show',
          params: {
            showId: show.id
          }
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [beDate, show, userPurchsaedShows]);

  const diffInSeconds =
    beDate !== undefined ? Math.floor((new Date(beDate).getTime() - now.getTime()) / 1000) : undefined;

  if (userShowsPending || allShowsPending) {
    return (
      <View style={styles.loaderContainer}>
        <DotsLoader />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.headerContainer}>
        <View style={styles.header}>
          <Text fontWeight="600" style={styles.subtitle}>
            {show?.description}
          </Text>
          <Text style={styles.releaseDate}>
            {i18n.t('trb_show.release_date', { DATE: formatDateYear(show?.releaseDate ?? '', 'MMMM do, yyyy') })}
          </Text>
          <Text
            fontWeight="600"
            style={[
              styles.price,
              {
                ...(influencerDiscountState &&
                  !influencerDiscountPending &&
                  influencerDiscountState.discountPercent > 0 &&
                  show &&
                  show.bsAppPrice &&
                  applyDiscount(parseFloat(show.bsAppPrice), influencerDiscountState.discountPercent) !==
                    show.bsAppPrice && {
                    textDecorationLine: 'line-through'
                  })
              }
            ]}>
            {i18n.t('trb_show.normal_price', { PRICE: show?.bsAppPrice })}
          </Text>
          {influencerDiscountState &&
            !influencerDiscountPending &&
            influencerDiscountState.discountPercent > 0 &&
            show &&
            show.bsAppPrice &&
            applyDiscount(parseFloat(show.bsAppPrice), influencerDiscountState.discountPercent) !== show.bsAppPrice && (
              <Text fontWeight="900" style={styles.specialPrice}>
                {i18n.t('trb_show.special_price', {
                  PRICE: applyDiscount(parseFloat(show.bsAppPrice), influencerDiscountState.discountPercent)
                })}
              </Text>
            )}
          <Text style={styles.taxes}>{i18n.t('trb_show.taxes_included')}</Text>
          <Text fontWeight="900" style={styles.trailer}>
            {i18n.t('trb_show.watch_trailer')}
          </Text>
        </View>
        <View style={styles.muxPlayerContainer}>
          <MuxPlayer
            ref={muxRef}
            theme="classic"
            style={styles.muxPlayer}
            playbackId={process.env.EXPO_PUBLIC_TRB_TRAILER_PLAYBACK_ID}
            castReceiver={CHROMECAST_APPLICATION_ID}
          />
        </View>
      </View>

      {/* If the show is purchased show countdown to release date */}
      {(userPurchsaedShows === undefined || userPurchsaedShows?.length === 0) &&
      show &&
      (!userShowsPending || !allShowsPending) ? (
        <BrandedButton
          style={styles.button}
          onPress={async () => {
            const showFinalPrice =
              influencerDiscountState !== undefined &&
              applyDiscount(parseFloat(show.bsAppPrice), influencerDiscountState.discountPercent) !== show.bsAppPrice
                ? applyDiscount(parseFloat(show.bsAppPrice), influencerDiscountState.discountPercent)
                : show.bsAppPrice;

            const payload = {
              routeWithSelectedMethod: '(app)/confirmPurchaseShow',
              availableMethods: 'BOTH',
              showId: show.id,
              showTitle: show.title,
              showPrice: showFinalPrice
            };

            const encrypted = await encryptObject(payload);
            router.navigate({
              pathname: '/paymentMethods',
              params: {
                payload: encodeURIComponent(encrypted)
              }
            });
          }}>
          <View style={styles.purchase}>
            <Text fontWeight="700" style={styles.purchaseText}>
              {i18n.t('trb_show.purchase')}
            </Text>
            <FontAwesome6 name="dollar-sign" size={18} color="white" />
          </View>
        </BrandedButton>
      ) : (
        <View style={styles.countdownContainer}>
          <Text fontWeight="700" style={styles.label}>
            {i18n.t('trb_show.release_date', { DATE: formatDateYear(show?.releaseDate ?? '', 'MMMM do, yyyy') })}
          </Text>
          <Countdown
            until={diffInSeconds ?? 0}
            size={24}
            onFinish={() => {
              router.replace({
                pathname: '/show',
                params: {
                  showId: show?.id
                }
              });
            }}
          />
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: 18,
    paddingBottom: 18,
    flexDirection: 'column',
    justifyContent: 'space-between',
    textAlign: 'center'
  },

  loaderContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center'
  },

  headerContainer: {
    height: '80%'
  },

  header: {
    width: '100%',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 5,
    textAlign: 'center'
  },

  subtitle: {
    fontSize: 18
  },

  releaseDate: {
    fontSize: 16
  },

  price: {
    color: 'black',
    fontSize: 16
  },

  taxes: {
    color: 'black',
    fontSize: 13
  },

  specialPrice: {
    color: 'green',
    fontSize: 18
  },

  trailer: {
    fontSize: 26,
    color: 'black'
  },

  muxPlayerContainer: {
    width: '100%',
    height: '75%',
    marginTop: 10
  },

  muxPlayer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    borderRadius: 10
  },

  button: {
    width: '100%'
  },

  purchase: {
    flexDirection: 'row',
    gap: 5,
    alignItems: 'center',
    justifyContent: 'center'
  },

  purchaseText: {
    color: 'white',
    textAlignVertical: 'center'
  },

  countdownContainer: {
    width: '100%',
    backgroundColor: '#000',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 5,
    borderRadius: 10
  },

  label: {
    color: '#fff',
    fontSize: 20,
    marginBottom: 10
  }
});

export default Purchase;
